import React, {
    FC,
    ReactNode,
    useMemo,
    useState,
    createContext,
    useContext, useCallback, useEffect,
} from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toggleLoading } from '../../store/ducks/pageConfig';
import { ThesaurusItemDetails } from '../../modules/consulta/components/SearchBar/thesaurus/types/ThesaurusItemDetails';
import { ThesaurusTipoObraType } from '../types/ThesaurusSearch';
import * as ThesaurusService from '../services/ThesaurusService';
import { APIHelper } from '../helpers';

interface ThesaurusContextProps {
    searchData: any
    changeSearchResults: (data: any) => void
    changeLegacyLoading:  (loading: boolean) => void
    searchOption:any,
    changeSearchOption: (searchOption: any) => void
    searchTherm:string|null
    changeSearchTherm: (searchTherm: string|null) => void;
    itemDetails: Array<Array<ThesaurusItemDetails>>
    changeItemDetails : ( itemDetails: Array<Array<ThesaurusItemDetails>>) => void
    tiposObra: ThesaurusTipoObraType[]
    codAutoridadeSelecionada: string|null
    descricaoAutoridadeSelecionada: string|null
    changeTiposObra: (codAutoridade: string, descricao: string, tiposObra: ThesaurusTipoObraType[]) => void
    salvarEstadoThesauro: () => void
    searchType:'term'|'tree',
    changeSearchType: (searchType: 'term'|'tree') => void
    openAll: boolean|undefined
    changeOpenAll: (openAll: boolean|undefined) => void
    hasSearched: boolean;
    search: (data: { searchTerm: string; searchOption: string }) => Promise<void>;
}

const CHAVE_ESTADO_THESAURO = 'estado-thesauro';

const ThesaurusContext = createContext<ThesaurusContextProps | undefined>(undefined);

interface ThesaurusContextProviderProps {
    children: ReactNode;
}

const ThesaurusContextProvider: FC<ThesaurusContextProviderProps> = ({ children }) => {
    const [searchData, setSearchData] = useState<any>(undefined);
    const [itemsChildren, setItemsChildren] = useState<any>(undefined);
    const [searchOption, setSearchOption] = useState(-1)
    const dispatch = useDispatch()
    const [searchTherm, setSearchTherm] = useState<string|null>(null)
    const [itemDetails, setItemDetails] = useState<Array<Array<ThesaurusItemDetails>>>([])
    const [searchType, setSearchType] = useState<'term'|'tree'>('term')
    const [openAll, setOpenAll] = useState<boolean|undefined>()
    const [hasSearched, setHasSearched] = useState<boolean>(false);
    const [tiposObra, setTiposObra] = useState<ThesaurusTipoObraType[]>([])
    const [codAutoridadeSelecionada, setCodAutoridadeSelecionada] = useState<string|null>(null)
    const [descricaoAutoridadeSelecionada, setDescricaoAutoridadeSelecionada] = useState<string|null>(null)
    const location = useLocation()

    const changeSearchResults = useCallback((searchData : any) => setSearchData(searchData) , [])
    const changeOpenAll = (openAll: boolean|undefined) => setOpenAll(openAll)
    const changeLegacyLoading = useCallback((loading: boolean) => dispatch(toggleLoading(loading)), [dispatch])
    const changeSearchOption = useCallback((searchOption: any) => setSearchOption(searchOption), [])
    const changeSearchType = (searchType: 'term'|'tree') => setSearchType(searchType)
    const changeSearchTherm = (searchTherm: string|null)=> setSearchTherm(searchTherm)
    const changeItemDetails = ( itemDetails: Array<Array<ThesaurusItemDetails>>) => setItemDetails(itemDetails)
    const changeTiposObra = (codAutoridade: string, descricao: string, tiposObra: ThesaurusTipoObraType[]) => {
        setCodAutoridadeSelecionada(codAutoridade)
        setDescricaoAutoridadeSelecionada(descricao)
        setTiposObra(tiposObra)
    }

    /**
     * Guarda o estado da tela antes de sair para o resultado de um tipo de obra,
     * para que o link "voltar ao thesauro" possa reconstruí-la.
     */
    const salvarEstadoThesauro = useCallback(() => {
        sessionStorage.setItem(CHAVE_ESTADO_THESAURO, JSON.stringify({
            searchData,
            searchOption,
            searchTherm,
            searchType,
            itemDetails,
            tiposObra,
            codAutoridadeSelecionada,
            descricaoAutoridadeSelecionada,
        }));
    }, [searchData, searchOption, searchTherm, searchType, itemDetails, tiposObra, codAutoridadeSelecionada, descricaoAutoridadeSelecionada])

    useEffect(() => {
        if (!location.search.includes('restaurar=true')) {
            return;
        }
        const estadoSalvo = sessionStorage.getItem(CHAVE_ESTADO_THESAURO);
        if (!estadoSalvo) {
            return;
        }
        const estado = JSON.parse(estadoSalvo);
        setSearchData(estado.searchData);
        setSearchOption(estado.searchOption);
        setSearchTherm(estado.searchTherm);
        setSearchType(estado.searchType);
        setItemDetails(estado.itemDetails ?? []);
        setTiposObra(estado.tiposObra ?? []);
        setCodAutoridadeSelecionada(estado.codAutoridadeSelecionada ?? null);
        setDescricaoAutoridadeSelecionada(estado.descricaoAutoridadeSelecionada ?? null);
        setHasSearched(true);
    }, []);

    const search = useCallback(async (data: { searchTerm: string; searchOption: string }) => {
        setSearchData(undefined);
        setHasSearched(false);
        setTiposObra([]);
        setCodAutoridadeSelecionada(null);
        setDescricaoAutoridadeSelecionada(null);
        changeLegacyLoading(true);
        ThesaurusService.getThesaurusSearch(
            data.searchTerm,
            data.searchOption,
        )
        .then(response => {
            const { data } = response;
            if (data) {
                setSearchData(data);
            }
        })
        .catch(error => {
            if (!axios.isCancel(error)) {
                APIHelper.tratarErro(error);
            }
        })
        .finally(() => {
            changeLegacyLoading(false);
            setHasSearched(true);
        });
    }, []);

    const memoizedData: ThesaurusContextProps = useMemo(
        () => ({
            searchData,
            changeSearchResults,
            changeLegacyLoading,
            searchOption,
            changeSearchOption,
            searchTherm,
            changeSearchTherm,
            itemDetails,
            changeItemDetails,
            tiposObra,
            codAutoridadeSelecionada,
            descricaoAutoridadeSelecionada,
            changeTiposObra,
            salvarEstadoThesauro,
            searchType,
            changeSearchType,
            openAll,
            changeOpenAll,
            hasSearched,
            search,
        }),
        [
            searchData,
            changeSearchResults,
            searchOption,
            searchTherm,
            itemDetails,
            tiposObra,
            codAutoridadeSelecionada,
            descricaoAutoridadeSelecionada,
            salvarEstadoThesauro,
            searchType,
            openAll,
            hasSearched,
        ],
    );

    return (
        <ThesaurusContext.Provider value={memoizedData}>
            {children}
        </ThesaurusContext.Provider>
    );
};

export const useThesaurusContext = (): ThesaurusContextProps => {
    const context = useContext(ThesaurusContext) as ThesaurusContextProps;
    if (context === undefined)
        throw new Error(
            'useThesaurusContext must be used within a ThesaurusContextProvider',
        );
    return context;
};

export default ThesaurusContextProvider;
